import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useAlertContext } from '@/contexts/AlertContext'
import { BID_LEVELS } from '@/features/bidding/mocks/bidLevels'
import type { BidLevel } from '@/features/bidding/types/bidLadder'

export type UseBidOfferCascadingBidLevelsResult = {
  bidLevels: BidLevel[]
  handleAddBidLevel: (volume: number, upPrice: number, downPrice: number) => void
  handleRemoveBidLevel: (index: number) => void
}

export function useBidOfferCascadingBidLevels(): UseBidOfferCascadingBidLevelsResult {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()

  // Bid level state management - includes both up and down markets
  const [bidLevels, setBidLevels] = useState<BidLevel[]>(BID_LEVELS)

  // Function to add new bid levels (both up and down)
  const handleAddBidLevel = useCallback(
    (volume: number, upPrice: number, downPrice: number) => {
      // Validation: Check max 6 levels per market
      const upBidsCount = bidLevels.filter((b) => b.market === 'up').length
      const downBidsCount = bidLevels.filter((b) => b.market === 'down').length

      if (upBidsCount >= 6 || downBidsCount >= 6) {
        pushAlert({
          message: t('bidding.bidding_cascading.bid_ladder.add_bid_level.error_maximum_bid_levels'),
          severity: 'error',
        })
        return
      }

      // Validation: Ensure same number of levels in both markets
      if (upBidsCount !== downBidsCount) {
        pushAlert({
          message: t('bidding.bidding_cascading.bid_ladder.add_bid_level.error_different_numbers'),
          severity: 'error',
        })
        return
      }

      // Create up market bid
      const upBid: BidLevel = {
        volumeMW: volume,
        cumulativeVolumeMW: 0, // Will be recalculated
        clearingProbability: 0.5, // Default value
        market: 'up',
        pricing: {
          type: 'fixed',
          fixedPrice: upPrice,
          avgPrice: upPrice,
        },
      }

      // Create down market bid with same volume (enforces pairing)
      const downBid: BidLevel = {
        volumeMW: volume,
        cumulativeVolumeMW: 0, // Will be recalculated
        clearingProbability: 0.5, // Default value
        market: 'down',
        pricing: {
          type: 'fixed',
          fixedPrice: downPrice,
          avgPrice: downPrice,
        },
      }

      // Add both bids and sort by market and price
      const updatedBidLevels = [...bidLevels, upBid, downBid].sort((a, b) => {
        // First sort by market
        if (a.market !== b.market) {
          return a.market === 'up' ? -1 : 1
        }
        // Then sort by price within each market
        const priceA = a.pricing.type === 'fixed' ? a.pricing.fixedPrice : a.pricing.avgPrice
        const priceB = b.pricing.type === 'fixed' ? b.pricing.fixedPrice : b.pricing.avgPrice
        return priceA - priceB
      })

      // Recalculate cumulative volumes for each market
      recalculateCumulativeVolumes(updatedBidLevels, 'up')
      recalculateCumulativeVolumes(updatedBidLevels, 'down')

      setBidLevels(updatedBidLevels)
    },
    [bidLevels, pushAlert],
  )

  // Function to remove a bid level (removes both up and down bids at same index)
  const handleRemoveBidLevel = useCallback(
    (index: number) => {
      // Find the pair of bids to remove (same volume, same position in sorted order)
      // For simplicity, we'll remove by index, but you might want to remove paired bids
      const bidToRemove = bidLevels[index]
      const volumeToRemove = bidToRemove.volumeMW
      const marketToRemove = bidToRemove.market

      // Remove the bid and its corresponding pair in the other market
      const updatedBidLevels = bidLevels.filter((bid, i) => {
        if (i === index) return false
        // Also remove the corresponding bid in the other market with same volume
        if (bid.market !== marketToRemove && bid.volumeMW === volumeToRemove) {
          // Find if this is the corresponding pair
          const samePriceLevel =
            bidLevels.filter((b) => b.market === bid.market && b.volumeMW === volumeToRemove).indexOf(bid) ===
            bidLevels.filter((b) => b.market === marketToRemove && b.volumeMW === volumeToRemove).indexOf(bidToRemove)
          if (samePriceLevel) return false
        }
        return true
      })

      // Recalculate cumulative volumes for each market
      recalculateCumulativeVolumes(updatedBidLevels, 'up')
      recalculateCumulativeVolumes(updatedBidLevels, 'down')

      setBidLevels(updatedBidLevels)
    },
    [bidLevels],
  )

  return useMemo(
    () => ({
      bidLevels,
      handleAddBidLevel,
      handleRemoveBidLevel,
    }),
    [bidLevels, handleAddBidLevel, handleRemoveBidLevel],
  )
}

// Function to recalculate cumulative volumes for a specific market
function recalculateCumulativeVolumes(bidLevels: BidLevel[], market: 'up' | 'down') {
  const marketBidLevels = bidLevels.filter((b) => b.market === market)
  let cumulative = 0
  for (const bid of marketBidLevels) {
    cumulative += bid.volumeMW
    bid.cumulativeVolumeMW = cumulative
  }
}
